// The learning day: XP days and streaks follow the learner's own midnight, not UTC. Used on the server (with the
// time zone stored on the account) and in the browser (with the device's own zone). Safe to import anywhere.

/** The zone used when an account has none stored yet: most learners are in Bharat. */
export const DEFAULT_TIME_ZONE = 'Asia/Kolkata'

const formatters = new Map<string, Intl.DateTimeFormat>()

const formatter = (timeZone: string): Intl.DateTimeFormat => {
    const known = formatters.get(timeZone)

    if (known) return known

    // en-CA formats a date as YYYY-MM-DD.
    const created = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })

    formatters.set(timeZone, created)

    return created
}

/** Whether a string is an IANA time zone this runtime knows (e.g. "Asia/Kolkata", "Europe/London"). */
export const validTimeZone = (value: unknown): value is string => {
    if (typeof value !== 'string' || value.length < 2 || value.length > 64 || !/^[A-Za-z0-9_+\-/]+$/.test(value)) return false

    try {
        formatter(value)

        return true
    }
    catch {
        return false
    }
}

/** A stored or sent zone, or the default when it is missing or unknown. */
export const zoneOrDefault = (value: unknown): string => (validTimeZone(value) ? value : DEFAULT_TIME_ZONE)

/** The calendar day (YYYY-MM-DD) of `date` in `timeZone`. */
export const dayIn = (timeZone?: string | null, date: Date = new Date()): string => {
    const parts = formatter(zoneOrDefault(timeZone)).formatToParts(date)
    const get = (type: string) => parts.find(p => p.type === type)?.value ?? ''

    return `${get('year')}-${get('month')}-${get('day')}`
}

/** `day` (YYYY-MM-DD) moved by `delta` calendar days. */
export const shiftDay = (day: string, delta: number): string => {
    const date = new Date(`${day}T12:00:00Z`)

    date.setUTCDate(date.getUTCDate() + delta)

    return date.toISOString().slice(0, 10)
}

/** The browser's own zone (call only in the browser, after mount). */
export const browserTimeZone = (): string => {
    try {
        return zoneOrDefault(Intl.DateTimeFormat().resolvedOptions().timeZone)
    }
    catch {
        return DEFAULT_TIME_ZONE
    }
}
